import { getLocale } from 'services/LocalizationService';
import priceMixin from 'aa/vue/plugins/priceMixin';

export default {
    mixins: [priceMixin],
    computed: {
        recurly() {
            return this.$store.state.payment.recurly;
        }
    },
    data() {
        return {
            pricing: null,
            coupon: null
        };
    },
    methods: {
        stripCountryCodeFromPromo(promoCode) {
            const split = promoCode.split('-');
            if (split.length >= 2 && split[split.length - 1].length === 2) {
                this.coupon.regionCode = split.pop().toLowerCase();
            }
        },
        setupRecurlyPricing(successFunction, errorFunction) {
            if (typeof this.recurly === 'object') {
                this.pricing = this.recurly.Pricing();

                this.pricing.on('set.coupon', (coupon) => {
                    const { code } = coupon;
                    this.coupon = coupon;

                    // Get region from coupon code
                    if (code) {
                        this.stripCountryCodeFromPromo(code);
                    }

                    successFunction(this.coupon);
                });

                this.pricing.on('error.coupon', (error) =>
                    errorFunction(this.couponError(error))
                );
            }
        },
        applyCoupon(planCode, couponCode) {
            if (this.pricing && planCode && couponCode) {
                this.pricing.plan(planCode).coupon(couponCode);
            }
        },
        checkForCouponErrors(coupon, countryCode, freeTrialBlocked = true) {
            let containsError = false;
            if (
                coupon.regionCode != null &&
                coupon.regionCode.toUpperCase() !== countryCode
            ) {
                containsError = 'd';
            } else if (
                coupon.discount.type == 'free_trial' &&
                freeTrialBlocked
            ) {
                containsError = 'a';
            } else if (coupon.discount.type == 'dollars') {
                if (
                    coupon.discount.amount[this.pricing.price.currency.code] ==
                        0 ||
                    coupon.discount.amount[this.pricing.price.currency.code] ==
                        null
                ) {
                    containsError = 'e';
                }
            } else if (coupon.redemption_resource !== 'subscription') {
                containsError = true;
            }
            return containsError;
        },

        /* 
    When we see a coupon with the language - "single_use" as the duration we need to check the plan
    to see if we're dealing with a monthly or annual plan. 
    if annual plan - return localized "first year"
    if monthly plan - return localized "first month"

    dependency on ability to check plan;
    */
        singleUseTerm(planType) {
            const tags = {
                monthly: 'first_month',
                annual: 'first_year'
            };
            return getLocale(tags[planType]) || '';
        },

        pluralizeDuration(amount, duration) {
            const durationTrim = duration.trim();

            return amount > 1 && !durationTrim.endsWith('s')
                ? (duration += 's')
                : duration;
        },

        currentSubscriptionCouponObjTransformer(currentSubscriptionCouponObj) {
            const {
                discountType,
                discountPercent,
                discountInCents,
                duration,
                temporalUnit,
                temporalAmount,
                freeTrialUnit,
                freeTrialAmount
            } = currentSubscriptionCouponObj;

            const mapCouponObj = {
                discount: {
                    type: discountType,
                    rate: discountPercent / 100 || null,
                    trial: {
                        unit: freeTrialUnit,
                        amount: freeTrialAmount
                    },
                    amount: discountInCents
                },
                duration: duration,
                temporal_unit: temporalUnit,
                temporal_amount: temporalAmount
            };

            return mapCouponObj;
        },

        /** Takes Recurly couponObj */
        formatCouponString(
            couponObj,
            currencySymbol,
            currencyCode,
            planType = 'monthly'
        ) {
            let couponString = '',
                amount = 0,
                duration = '';

            if (
                !couponObj['temporal_unit'] &&
                !couponObj['temporal_amount'] &&
                couponObj['discount']['type'] == 'free_trial'
            ) {
                amount = parseInt(couponObj['discount']['trial']['amount']);
                duration = couponObj['discount']['trial']['unit'];
                duration = this.pluralizeDuration(amount, duration);
                couponString = `${amount} ${getLocale(duration)} ${getLocale(
                    'free'
                )}`;
            }

            // discount $ amount
            else if (
                couponObj['discount'] &&
                couponObj['discount']['type'] &&
                couponObj['discount']['type'] == 'dollars'
            ) {
                // Fixed Amount coupon
                amount = couponObj['discount']['amount'][currencyCode];

                amount = this.pricePrettyPrint(
                    amount,
                    currencySymbol,
                    currencyCode,
                    'en-US',
                    false
                );

                couponString = `${amount} ${getLocale('off')}`;
            }
            // discount % amount
            else {
                if (couponObj['temporal_unit']) {
                    amount = parseInt(couponObj['temporal_amount']);
                }
                if (
                    couponObj['duration'] &&
                    (couponObj['duration'] === 'forever' ||
                        couponObj['duration'] === 'single_use')
                ) {
                    duration = couponObj['duration'];
                } else {
                    duration = couponObj['temporal_unit']
                        ? couponObj['temporal_unit']
                        : '';
                }
                duration = this.pluralizeDuration(amount, duration);
                const percent_discount = `${couponObj.discount.rate * 100}%`;
                if (amount > 0) {
                    couponString = getLocale('percent_discount_over_time', {
                        percent: percent_discount,
                        time:
                            amount > 0
                                ? `${amount} ${getLocale(duration)}`
                                : `${getLocale(duration)}`
                    });
                } else {
                    if (duration === 'forever') {
                        couponString = `${percent_discount} ${getLocale(
                            'off'
                        )}`;
                    } else if (duration === 'single_use') {
                        couponString = getLocale(
                            'percent_discount_first_term',
                            {
                                percent: percent_discount,
                                first_term: this.singleUseTerm(planType)
                            }
                        );
                    }
                }
            }
            return couponString;
        },

        couponError(res) {
            let errorString = '';

            switch (res) {
                case 'a': // Free trial not available to existing subscribers
                    errorString =
                        'recurly_this_promotional_code_is_valid_for_new_subscriptions_only';
                    break;
                case 'b': // This coupon has expired // this is currently uncatchable will fallback to default
                    errorString = 'recurly_this_promotional_code_has_expired';
                    break;
                case 'c': // This one use coupon has already been redeemed
                    errorString =
                        'recurly_this_promotional_code_has_already_been_redeemed';
                    break;
                case 'd': // This coupon is not available in your region
                    errorString =
                        'recurly_this_promotional_code_is_not_supported_in_your_country';
                    break;
                case 'e': // This coupon is not available in your region for currency regions
                    errorString =
                        'recurly_this_promotional_code_is_not_in_the_same_currency_as_your_subscription';
                    break;
                default:
                    //  error all other issues
                    errorString = 'recurly_this_promotional_code_is_not_valid';
            }
            return errorString;
        }
    }
};
